import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '../shared/Icon';
import Chip from '../shared/Chip';
import Timestamp from '../shared/Timestamp';
import { useFlag } from '@unleash/proxy-client-react';
import './Automations.css';
import AutomationWizard from '../AutomationWizard/AutomationWizard';
import ReportsAutomationWizard from '../AutomationWizard/ReportsAutomationWizard';
import RunHistoryDrawer from './RunHistoryDrawer';
import AlertsAutomationWizard from '../AutomationWizard/AlertsAutomationWizard';
import AlertDetailModal from './AlertDetailModal';
import { apiService } from '../../../services/api';
import { isAlert } from './automationType';
import { allAutomationDatasetIds, datasetsForAutomation } from './automationDatasets';
import { useReviewRequiredDatasets } from '../AutomationWizard/useReviewRequiredDatasets';
import { reviewRequiredDatasets, REVIEW_REQUIRED_LABEL } from '../../../utils/reviewRequiredNotice';
import { useConfirm } from '../shared/useConfirm';
import Pagination from '../shared/Pagination';
import { storedScheduleLabel } from '../../../utils/scheduleLabel';

const Automations = ({ onShowNotification, openWizard = false, onWizardClose, initialReportQuery = '', initialReportResponse = '', initialReportThreadId = '', initialReportDatasetIds = [], initialRunAutomationId = '', initialRunId = '', onRunViewConsumed }) => {
  // When OFF, only Ingest and Reports automation tabs/types are visible
  // (plus the "All" aggregator). Publish, Join, Quality, Alerts, Govern,
  // Review, and Custom are hidden until the flag is enabled.
  const extraAutomationTypesEnabled = useFlag('enable_extra_automation_types');
  // Alerts has its own flag rather than riding on the extras flag: the extras
  // are placeholder templates with no implementation behind them, and Alerts is
  // a real surface that needs to be switched on deliberately (PRD-1248).
  const alertsEnabled = useFlag('enable_alert_automations');
  const [wizardOpen, setWizardOpen] = useState(openWizard);
  const [wizardVariant, setWizardVariant] = useState(initialReportQuery ? 'reports' : 'generic');
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [runningAutomation, setRunningAutomation] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  // Run to auto-open + render when arriving from a report email deep-link
  // (/automations?automation=…&run=…), see PRD-1269.
  const [focusRunId, setFocusRunId] = useState('');
  // Overflow-menu state mirrors the dataset card grid: one openMenuId for the
  // whole list + a single outside-click handler closes it.
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [confirm, confirmDialog] = useConfirm();
  // PRD-1359 (Figma "Automations"): the Review required chip on a card and the
  // banner in its run history need the dataset RECORDS behind each automation —
  // the list endpoint hands back ids only. One load for the page, polled while
  // any of them is still settling.
  const reviewDatasets = useReviewRequiredDatasets(allAutomationDatasetIds(automations));
  const needsReview = (automation) =>
    reviewRequiredDatasets(datasetsForAutomation(automation, reviewDatasets)).length > 0;
  const pageSize = 20;

  useEffect(() => {
    if (!openMenuId) return;
    const onClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenuId(null);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [openMenuId]);

  const handleTogglePause = async (automation) => {
    setOpenMenuId(null);
    try {
      await apiService.updateAutomation(automation.id, { enabled: !automation.enabled });
      if (onShowNotification) {
        onShowNotification(automation.enabled ? 'Automation paused' : 'Automation resumed', 'success');
      }
      loadAutomations(page);
    } catch (error) {
      console.error('Failed to toggle pause:', error);
      if (onShowNotification) onShowNotification(error.message || 'Failed to update automation', 'error');
    }
  };

  // RunHistory rows cascade on delete (FK ON DELETE CASCADE) — destructive.
  const handleDeleteAutomation = async (automation) => {
    setOpenMenuId(null);
    const ok = await confirm({
      title: 'Delete automation',
      message:
        `Delete automation "${automation.name}"?\n\n` +
        `Run history for this automation will also be removed. This action cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await apiService.deleteAutomation(automation.id);
      if (onShowNotification) onShowNotification('Automation deleted', 'success');
      const remaining = total - 1;
      const lastPage = Math.max(1, Math.ceil(remaining / pageSize));
      loadAutomations(Math.min(page, lastPage));
    } catch (error) {
      console.error('Failed to delete automation:', error);
      if (onShowNotification) onShowNotification(error.message || 'Failed to delete automation', 'error');
    }
  };

  useEffect(() => {
    if (openWizard) {
      setWizardOpen(true);
      setWizardVariant(initialReportQuery ? 'reports' : (activeTab === 'reports' ? 'reports' : 'generic'));
    }
  }, [openWizard, initialReportQuery, activeTab]);

  // Report email deep-link: /automations?automation=…&run=… opens that
  // automation's run-history drawer and renders the delivered report (PRD-1269).
  useEffect(() => {
    if (!initialRunAutomationId) return;
    // Wait for the automations list before consuming, so `match` gets a real
    // chance to resolve the automation's name; the stub is then a genuine
    // "not on this page" fallback rather than what every deep-link sees.
    if (loading) return;
    const match = automations.find((a) => String(a.id) === String(initialRunAutomationId));
    // Fall back to a minimal stub when the run's automation isn't on the current
    // page — the drawer only needs an id + name to fetch and render its runs.
    setSelectedAutomation(match || { id: initialRunAutomationId, name: 'Report' });
    setFocusRunId(initialRunId || '');
    if (onRunViewConsumed) onRunViewConsumed();
  }, [initialRunAutomationId, initialRunId, automations, loading, onRunViewConsumed]);

  const loadAutomations = useCallback(async (pageNum = 1) => {
    try {
      setLoading(true);
      const type = activeTab === 'all' ? null : activeTab.toLowerCase();
      const response = await apiService.getAutomations({ type, page: pageNum, page_size: pageSize });
      const list = response?.items ?? (Array.isArray(response) ? response : []);
      setAutomations(list);
      setTotal(response?.total ?? list.length);
      setPage(pageNum);
    } catch (error) {
      console.error('Error loading automations:', error);
      setAutomations([]);
      setTotal(0);
      if (onShowNotification) {
        onShowNotification(error.message || 'Failed to load automations', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [activeTab, onShowNotification]);

  useEffect(() => {
    loadAutomations(1);
  }, [loadAutomations]);

  const handleCreateAutomation = () => {
    // Gated on the handler as well as the JSX — the tab cannot be reached with
    // the flag off, but the variant must not resolve to 'alerts' regardless.
    if (activeTab === 'alerts' && alertsEnabled) {
      setWizardVariant('alerts');
    } else {
      setWizardVariant(activeTab === 'reports' ? 'reports' : 'generic');
    }
    setWizardOpen(true);
  };

  const handleCloseWizard = () => {
    setWizardOpen(false);
    if (onWizardClose) {
      onWizardClose();
    }
    // Reload automations after wizard closes
    loadAutomations();
  };

  const handleRunNow = async (automation) => {
    try {
      setRunningAutomation(automation.id);
      const response = await apiService.runAutomation(automation.id);
      const jobId = response?.job_id;

      if (onShowNotification) {
        onShowNotification('Automation started successfully', 'success');
      }

      // Poll for job completion if we have a job_id, otherwise clear running state
      if (jobId) {
        pollJobStatus(jobId, automation.name);
      } else {
        setRunningAutomation(null);
      }

      loadAutomations(page);
    } catch (error) {
      console.error('Error running automation:', error);
      if (onShowNotification) {
        onShowNotification(error.message || 'Failed to run automation', 'error');
      }
      setRunningAutomation(null);
    }
  };

  const pollJobStatus = async (jobId, automationName) => {
    const maxAttempts = 120;
    let attempts = 0;

    const clearAndNotify = (message, type) => {
      setRunningAutomation(null);
      if (onShowNotification) {
        onShowNotification(message, type);
      }
    };

    const poll = async () => {
      try {
        const statusResponse = await apiService.getJobStatus(jobId);
        const status = statusResponse?.status;

        if (status === 'complete') {
          clearAndNotify(`Automation "${automationName}" has completed successfully`, 'success');
          window.dispatchEvent(new CustomEvent('automation-completed', {
            detail: { automationName, jobId }
          }));
          return;
        } else if (status === 'failed') {
          clearAndNotify(`Automation "${automationName}" has failed`, 'error');
          return;
        } else if (status === 'queued' || status === 'processing') {
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, 5000);
          } else {
            setRunningAutomation(null);
            console.warn(`Job ${jobId} polling timeout after ${maxAttempts} attempts`);
          }
        }
      } catch (error) {
        console.error('Error polling job status:', error);
        setRunningAutomation(null);
      }
    };

    setTimeout(poll, 5000);
  };

  const formatSchedule = storedScheduleLabel;

  if (editingAutomation && isAlert(editingAutomation) && alertsEnabled) {
    return (
      <AlertsAutomationWizard
        key={`edit-alert-${editingAutomation.id}`}
        isOpen
        onClose={() => {
          setEditingAutomation(null);
          loadAutomations(page);
        }}
        onShowNotification={onShowNotification}
        editingAutomation={editingAutomation}
      />
    );
  }

  if (editingAutomation) {
    return (
      <AutomationWizard
        key={`edit-${editingAutomation.id}`}
        isOpen={true}
        onClose={() => {
          setEditingAutomation(null);
          loadAutomations(page);
        }}
        onShowNotification={onShowNotification}
        editingAutomation={editingAutomation}
      />
    );
  }

  // Show wizard when open. When opening from chat "Schedule as report", pass initialStep=2 and initialReportQuery to AutomationWizard so it opens at step 2 (Query).
  if (wizardOpen && wizardVariant === 'alerts' && alertsEnabled) {
    return (
      <AlertsAutomationWizard
        key="new-alert"
        isOpen={wizardOpen}
        onClose={handleCloseWizard}
        onShowNotification={onShowNotification}
      />
    );
  }

  const showReportsWizard = wizardOpen && (
    wizardVariant === 'reports' ||
    (initialReportQuery != null && String(initialReportQuery).trim() !== '')
  );
  if (wizardOpen) {
    const fromChat = initialReportQuery && String(initialReportQuery).trim();
    if (showReportsWizard && !fromChat) {
      // New report from Reports tab (no query from chat) → use Reports wizard starting at step 1
      return (
        <ReportsAutomationWizard
          key="new-report"
          isOpen={wizardOpen}
          onClose={handleCloseWizard}
          onShowNotification={onShowNotification}
          initialQuery=""
          initialStep={1}
        />
      );
    }
    if (fromChat) {
      // From chat "Schedule as report" → use main AutomationWizard with initialStep=2 and initialReportQuery (opens at Query step)
      return (
        <AutomationWizard
          key="schedule-from-chat"
          isOpen={wizardOpen}
          onClose={handleCloseWizard}
          onShowNotification={onShowNotification}
          initialStep={2}
          initialReportQuery={initialReportQuery}
          initialReportResponse={initialReportResponse}
          initialReportThreadId={initialReportThreadId}
          initialReportDatasetIds={initialReportDatasetIds}
        />
      );
    }
    return (
      <AutomationWizard
        isOpen={wizardOpen}
        onClose={handleCloseWizard}
        onShowNotification={onShowNotification}
      />
    );
  }

  return (
    <div className="automations-page">
      {confirmDialog}
      <div className="automations-header">
        <button className="automations-new-button" onClick={handleCreateAutomation}>
          {activeTab === 'alerts' ? 'New alert' : activeTab === 'reports' ? 'New report' : 'New Automation'}
        </button>
      </div>

      <div className="automations-tabs">
        <button
          className={`automations-tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All
        </button>
        <button
          className={`automations-tab ${activeTab === 'ingest' ? 'active' : ''}`}
          onClick={() => setActiveTab('ingest')}
        >
          Ingest
        </button>
        {extraAutomationTypesEnabled && (
          <button
            className={`automations-tab ${activeTab === 'publish' ? 'active' : ''}`}
            onClick={() => setActiveTab('publish')}
          >
            Publish
          </button>
        )}
        {extraAutomationTypesEnabled && (
          <button
            className={`automations-tab ${activeTab === 'join' ? 'active' : ''}`}
            onClick={() => setActiveTab('join')}
          >
            Join
          </button>
        )}
        {extraAutomationTypesEnabled && (
          <button
            className={`automations-tab ${activeTab === 'quality' ? 'active' : ''}`}
            onClick={() => setActiveTab('quality')}
          >
            Quality
          </button>
        )}
        {alertsEnabled && (
          <button
            className={`automations-tab ${activeTab === 'alerts' ? 'active' : ''}`}
            onClick={() => setActiveTab('alerts')}
          >
            Alerts
          </button>
        )}
        <button
          className={`automations-tab ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('reports')}
        >
          Reports
        </button>
        {extraAutomationTypesEnabled && (
          <button
            className={`automations-tab ${activeTab === 'govern' ? 'active' : ''}`}
            onClick={() => setActiveTab('govern')}
          >
            Govern
          </button>
        )}
        {extraAutomationTypesEnabled && (
          <button
            className={`automations-tab ${activeTab === 'review' ? 'active' : ''}`}
            onClick={() => setActiveTab('review')}
          >
            Review
          </button>
        )}
      </div>

      <div className="automations-content">
        {/* Only blank to the loading state on the FIRST load (no cached data).
            On any later revalidation keep rendering the cached grid so clicking
            a card / background refreshes don't flash the list (PRD-1119). */}
        {loading && automations.length === 0 ? (
          <div className="automations-loading">
            <p>Loading automations...</p>
          </div>
        ) : automations.length === 0 ? (
          <div className="automations-empty-state">
            <p>No automations yet. Click "New Automation" to create your first automation.</p>
          </div>
        ) : (
          <>
          <div className="resui-card-grid">
            {automations.map((automation) => {
              return (
                <div
                  key={automation.id}
                  className="resui-card resui-card--interactive automation-card"
                  role="button"
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedAutomation(automation)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedAutomation(automation);
                    }
                  }}
                >
                  <div className="automation-card-header">
                    <h3 className="automation-card-title">{automation.name}</h3>
                    {!automation.enabled && automation.dataset_deleted && (
                      <div
                        className="automation-deleted-dataset-icon"
                        data-tooltip="The linked dataset has been deleted. Please update the dataset before resuming this automation."
                        role="img"
                        aria-label="The linked dataset has been deleted. Please update the dataset before resuming this automation."
                      >
                        <Icon name="error" />
                      </div>
                    )}
                    {/* Pause/Resume label flips on `automation.enabled` so it
                        always reflects current state. */}
                    <div
                      className="data-sources-card-actions"
                      ref={openMenuId === automation.id ? menuRef : null}
                      style={{ position: 'relative', marginLeft: 'auto' }}
                    >
                      <button
                        type="button"
                        className="data-sources-card-menu-button"
                        aria-label="Automation actions"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId(prev => prev === automation.id ? null : automation.id);
                        }}
                      >
                        <Icon name="more_vert" />
                      </button>
                      {openMenuId === automation.id && (
                        <div className="data-sources-card-menu-dropdown">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuId(null);
                              // Gated: with the flag off an alert would fall
                              // through to the generic 6-step wizard, which
                              // maps no alert fields at all.
                              if (isAlert(automation) && !alertsEnabled) return;
                              setEditingAutomation(automation);
                            }}
                          >
                            <Icon name="edit" /> Edit
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTogglePause(automation);
                            }}
                          >
                            <Icon name={automation.enabled ? 'pause' : 'play_arrow'} />
                            {automation.enabled ? ' Pause' : ' Resume'}
                          </button>
                          <button
                            className="data-sources-menu-delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAutomation(automation);
                            }}
                          >
                            <Icon name="delete" /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {automation.description && (
                    <p className="automation-card-description">{automation.description}</p>
                  )}
                  <div className="automation-card-details">
                    <div className="automation-detail-item">
                      {/* An alert has two independent states: `enabled` is the
                          scheduler's, `alert_active` is the alert's own. An
                          enabled-but-inactive alert sends nothing, so showing
                          "Active" off `enabled` alone would be wrong. */}
                      {isAlert(automation) ? (
                        <Chip
                          variant={automation.enabled && automation.alert_active ? 'success' : 'alert'}
                          className="automation-status"
                        >
                          {!automation.enabled
                            ? 'Paused'
                            : automation.alert_active ? 'Active' : 'Inactive'}
                        </Chip>
                      ) : (
                        <Chip
                          variant={automation.enabled ? 'success' : 'alert'}
                          className="automation-status"
                        >
                          {automation.enabled ? 'Active' : 'Paused'}
                        </Chip>
                      )}
                      {/* Beside the status, per the frame: this automation reads a
                          dataset with documents parked for review, so its next run
                          would compute on partial data. A marker, not a block. */}
                      {needsReview(automation) && (
                        <Chip variant="alert" className="automation-status review-required">{REVIEW_REQUIRED_LABEL}</Chip>
                      )}
                    </div>
                    <div className="automation-detail-item">
                      <span className="automation-detail-label">Trigger:</span>
                      <span className="automation-detail-value">{formatSchedule(automation)}</span>
                    </div>
                    <div className="automation-detail-item">
                      <span className="automation-detail-label">Last run:</span>
                      <span className="automation-detail-value">
                        {automation.last_run_at ? <Timestamp value={automation.last_run_at} /> : 'Not run yet'}
                      </span>
                    </div>
                    {!automation.dataset_id && automation.dataset_name && automation.dataset_action === 'create' && (
                      <div className="automation-detail-item">
                        <span className="automation-detail-label">Dataset:</span>
                        <span className="automation-detail-value" style={{ color: 'var(--color-typography-text-grey)' }}>
                          {automation.dataset_name}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="automation-card-actions">
                    <button
                      className="resui-btn resui-btn--tertiary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRunNow(automation);
                      }}
                      disabled={runningAutomation === automation.id}
                    >
                      <span className="resui-btn-label">{runningAutomation === automation.id ? 'Running…' : 'Run Now'}</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <Pagination
            currentPage={page}
            totalCount={total}
            pageSize={pageSize}
            onPageChange={loadAutomations}
          />
          </>
        )}
      </div>
      {/* An alert discloses in a modal; every other type keeps the run-history
          drawer. A deliberate departure, not an inconsistency (PRD-1248 AC3).

          Gated on the flag as well as the type: the All tab passes type: null,
          so alert rows list even with the flag off, and without this, clicking
          one opened the modal's live Activate button.

          The drawer branch keeps `focusRunId` and its reset — PRD-1269's report
          email deep-link (/automations?automation=…&run=…) depends on both, and
          an alert never arrives by that route. */}
      {isAlert(selectedAutomation) && alertsEnabled ? (
        <AlertDetailModal
          automation={selectedAutomation}
          onClose={() => setSelectedAutomation(null)}
          onShowNotification={onShowNotification}
          onChanged={() => loadAutomations(page)}
          onEdit={(automation) => {
            setSelectedAutomation(null);
            setEditingAutomation(automation);
          }}
        />
      ) : (
        <RunHistoryDrawer
          automation={selectedAutomation}
          reviewDatasets={datasetsForAutomation(selectedAutomation, reviewDatasets)}
          isOpen={!!selectedAutomation}
          onClose={() => {
            setSelectedAutomation(null);
            setFocusRunId('');
          }}
          focusRunId={focusRunId}
          onShowNotification={onShowNotification}
        />
      )}

    </div>
  );
};

export default Automations;
